// ---------------------------------------------------------------------------
// queue.ts — what plays next
// ---------------------------------------------------------------------------
//
// Pure functions over an explicit state object, so "does shuffle reach every
// track before repeating one?" is a test rather than a thing you find out during
// a shift.
//
// Two decisions worth stating, because both are places a player commonly feels
// broken:
//
//   Shuffle is a PERMUTATION, not a random pick per advance. Picking randomly
//   each time replays the same track two songs later and never reaches others —
//   it feels broken without being wrong in any single step.
//
//   `repeat: 'one'` applies to a track ENDING, not to pressing next. A user
//   pressing next while repeat-one is on wants the next track; a player that
//   restarts the same one instead appears frozen.
// ---------------------------------------------------------------------------

export type RepeatMode = 'off' | 'one' | 'all';

export interface QueueState {
  /** Track ids in the order they were added. */
  ids: readonly string[];
  /** Index into `ids`, or -1 when nothing is loaded. */
  currentIndex: number;
  shuffle: boolean;
  /**
   * A permutation of `0..ids.length-1` giving the shuffled play order.
   * Only read when `shuffle` is true.
   */
  shuffleOrder: readonly number[];
  repeat: RepeatMode;
}

/** Why the queue is advancing. */
export interface AdvanceReason {
  /** True when a track ended by itself, false when the user pressed next/prev. */
  auto: boolean;
}

/**
 * A Fisher-Yates shuffle of `0..length-1`, with the current track first.
 *
 * Putting `startAt` first matters: enabling shuffle mid-track must not jump away
 * from what is playing. The rest follows in random order.
 *
 * `rng` is injected so the test can assert the permutation property against a
 * deterministic sequence instead of hoping.
 */
export function buildShuffleOrder(
  length: number,
  startAt: number = -1,
  rng: () => number = Math.random,
): number[] {
  const order = Array.from({ length }, (_, i) => i);
  for (let i = length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  if (startAt >= 0 && startAt < length) {
    const at = order.indexOf(startAt);
    if (at > 0) [order[0], order[at]] = [order[at], order[0]];
  }
  return order;
}

/** Position of `currentIndex` within the shuffled order, or -1. */
function shufflePosition(state: QueueState): number {
  return state.shuffleOrder.indexOf(state.currentIndex);
}

/**
 * The index to play after this one, or null when the queue is finished.
 *
 * Null is meaningful: the caller stops rather than silently looping. Looping
 * without `repeat: 'all'` is the single most annoying thing a player can do.
 */
export function nextIndex(state: QueueState, reason: AdvanceReason = { auto: false }): number | null {
  const size = state.ids.length;
  if (size === 0) return null;
  if (state.currentIndex < 0) return firstIndex(state);

  // Repeat-one only holds when a track ended on its own.
  if (reason.auto && state.repeat === 'one') return state.currentIndex;

  if (state.shuffle && state.shuffleOrder.length === size) {
    const pos = shufflePosition(state);
    if (pos === -1) return firstIndex(state);
    if (pos + 1 < size) return state.shuffleOrder[pos + 1];
    return state.repeat === 'all' ? state.shuffleOrder[0] : null;
  }

  if (state.currentIndex + 1 < size) return state.currentIndex + 1;
  return state.repeat === 'all' ? 0 : null;
}

/**
 * The index before this one, or null.
 *
 * Deliberately does NOT honour repeat-one: pressing previous is always a
 * navigation, never a restart.
 */
export function previousIndex(state: QueueState): number | null {
  const size = state.ids.length;
  if (size === 0) return null;
  if (state.currentIndex < 0) return firstIndex(state);

  if (state.shuffle && state.shuffleOrder.length === size) {
    const pos = shufflePosition(state);
    if (pos === -1) return firstIndex(state);
    if (pos - 1 >= 0) return state.shuffleOrder[pos - 1];
    return state.repeat === 'all' ? state.shuffleOrder[size - 1] : null;
  }

  if (state.currentIndex - 1 >= 0) return state.currentIndex - 1;
  return state.repeat === 'all' ? size - 1 : null;
}

/** The first index to play from a standing start. */
export function firstIndex(state: Pick<QueueState, 'ids' | 'shuffle' | 'shuffleOrder'>): number | null {
  if (state.ids.length === 0) return null;
  if (state.shuffle && state.shuffleOrder.length === state.ids.length) {
    return state.shuffleOrder[0];
  }
  return 0;
}

/**
 * Walk forward past tracks that cannot play, so one unreadable file does not
 * stall the queue.
 *
 * `isPlayable` is passed in rather than imported so this stays independent of how
 * availability is determined. Bounded by the queue length: with `repeat: 'all'`
 * and nothing playable, an unbounded walk would spin forever.
 */
export function nextPlayableIndex(
  state: QueueState,
  isPlayable: (id: string) => boolean,
  reason: AdvanceReason = { auto: false },
): number | null {
  let cursor = state;
  for (let steps = 0; steps < state.ids.length; steps++) {
    const candidate = nextIndex(cursor, steps === 0 ? reason : { auto: false });
    if (candidate === null) return null;
    if (isPlayable(state.ids[candidate])) return candidate;
    cursor = { ...cursor, currentIndex: candidate };
  }
  return null;
}

/**
 * Remove one entry, keeping the right track playing.
 *
 * Removing the entry BEFORE the current one shifts it down by one; removing the
 * current one leaves the index pointing at what slid into its place, which is
 * what a user expects — the list closes up and the next track takes over.
 */
export function removeAt(state: QueueState, index: number): QueueState {
  if (index < 0 || index >= state.ids.length) return state;

  const ids = [...state.ids.slice(0, index), ...state.ids.slice(index + 1)];

  let currentIndex = state.currentIndex;
  if (index < state.currentIndex) currentIndex -= 1;
  else if (index === state.currentIndex) {
    currentIndex = currentIndex >= ids.length ? ids.length - 1 : currentIndex;
  }
  if (ids.length === 0) currentIndex = -1;

  return {
    ...state,
    ids,
    currentIndex,
    // Indices shifted, so any existing permutation is stale.
    shuffleOrder: state.shuffle ? buildShuffleOrder(ids.length, currentIndex) : [],
  };
}

/** Move an entry, keeping the same track playing. */
export function moveItem(state: QueueState, from: number, to: number): QueueState {
  const size = state.ids.length;
  if (from < 0 || from >= size || to < 0 || to >= size || from === to) return state;

  const ids = [...state.ids];
  const [moved] = ids.splice(from, 1);
  ids.splice(to, 0, moved);

  // Follow the playing track to its new position rather than keeping the index.
  let currentIndex = state.currentIndex;
  if (currentIndex === from) currentIndex = to;
  else if (from < currentIndex && to >= currentIndex) currentIndex -= 1;
  else if (from > currentIndex && to <= currentIndex) currentIndex += 1;

  return { ...state, ids, currentIndex };
}

/** Append entries, skipping ids already queued so a double-click cannot duplicate. */
export function appendUnique(state: QueueState, newIds: readonly string[]): QueueState {
  const existing = new Set(state.ids);
  const additions = newIds.filter((id) => !existing.has(id));
  if (additions.length === 0) return state;

  const ids = [...state.ids, ...additions];
  return {
    ...state,
    ids,
    shuffleOrder: state.shuffle ? buildShuffleOrder(ids.length, state.currentIndex) : [],
  };
}
